import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Tag } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Blog categories
const categories = [
  { id: 'all', label: 'Todos' },
  { id: 'dieta-perros', label: 'Dieta natural para perros' },
  { id: 'dieta-gatos', label: 'Dieta natural para gatos' },
  { id: 'porciones', label: 'Porciones y rutinas' },
  { id: 'transicion', label: 'Transición y tolerancia' },
  { id: 'ingredientes', label: 'Etiquetas e ingredientes' },
];

// Placeholder blog posts
const placeholderPosts = [
  {
    id: '1',
    slug: 'como-empezar-dieta-natural-perro',
    title: 'Cómo empezar con la dieta natural para tu perro: guía para principiantes',
    excerpt: 'Todo lo que necesitas saber para dar los primeros pasos hacia una alimentación más saludable para tu perro.',
    category: 'dieta-perros',
    date: '2024-01-15',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1692906456159-f93a6f3df407?w=600',
  },
  {
    id: '2',
    slug: 'gatos-selectivos-alimentacion-natural',
    title: 'Alimentación natural para gatos selectivos: estrategias que funcionan',
    excerpt: 'Los gatos pueden ser muy exigentes con la comida. Descubre cómo introducir alimentos naturales sin frustrarte.',
    category: 'dieta-gatos',
    date: '2024-01-10',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1668979323445-11d84f1be907?w=600',
  },
  {
    id: '3',
    slug: 'calcular-porciones-comida-natural',
    title: 'Cómo calcular las porciones de comida natural para tu mascota',
    excerpt: 'Una de las dudas más frecuentes: ¿cuánta comida natural debo dar? Te explico los factores a considerar.',
    category: 'porciones',
    date: '2024-01-05',
    readTime: '7 min',
    image: 'https://images.unsplash.com/photo-1602470521011-4b18a8ea292b?w=600',
  },
];

export const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState(placeholderPosts);

  useEffect(() => {
    // Fetch posts from backend
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/blog/posts`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setPosts(data);
          }
        }
      } catch {
        // Using placeholder posts as fallback
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = activeCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === activeCategory);

  return (
    <>
      <SEOHead
        title="Blog | REALimenta"
        description="Consejos prácticos de nutrición natural para perros y gatos: transición, hábitos, porciones e ingredientes."
        canonical="/blog"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Blog' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mb-12"
          >
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
              data-testid="blog-h1"
            >
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
              >
                Blog
              </span>
            </h1>
            <p className="text-text-secondary text-lg md:text-xl">
              Artículos, consejos y guías sobre nutrición natural para perros y gatos.
            </p>
          </motion.div>

          {/* Categories */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap gap-2 mb-12"
            data-testid="blog-categories"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-brand-primary text-white'
                    : 'bg-brand-sage/30 text-text-secondary hover:bg-brand-sage/50'
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.label}
              </button>
            ))}
          </motion.div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-brand group overflow-hidden"
                data-testid={`blog-post-${post.id}`}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-video rounded-xl overflow-hidden mb-4 -mx-2 -mt-2">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <span className="flex items-center gap-1 text-xs text-text-muted">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.date).toLocaleDateString('es-ES', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-text-muted">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h2 className="font-manrope font-bold text-lg text-text-primary mb-2 group-hover:text-brand-primary transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                  
                  <span className="inline-flex items-center gap-1 text-xs text-brand-accent">
                    <Tag className="w-3 h-3" />
                    {categories.find(c => c.id === post.category)?.label || post.category}
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <p className="text-center text-text-muted py-12">
              No hay artículos en esta categoría todavía.
            </p>
          )}

          {/* Ideas for future articles */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 p-8 bg-brand-sage/20 rounded-3xl"
          >
            <h3 className="font-manrope text-xl font-bold text-text-primary mb-4">
              Próximos artículos
            </h3>
            <p className="text-text-muted text-sm mb-6">
              Estamos preparando más contenido sobre:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 text-sm text-text-secondary">
              <li>• Proteínas ideales para perros con alergias</li>
              <li>• BARF vs. dieta cocinada: pros y contras</li>
              <li>• Cómo leer etiquetas de alimentos comerciales</li>
              <li>• Nutrición para perros senior</li>
              <li>• Huesos recreativos: sí o no</li>
              <li>• Suplementos esenciales en dieta natural</li>
              <li>• Dieta natural para gatos con enfermedad renal</li>
              <li>• Mitos sobre la alimentación cruda</li>
              <li>• Batch cooking: organiza las comidas de la semana</li>
              <li>• Control de peso con dieta natural</li>
              <li>• Alimentación natural para cachorros</li>
              <li>• Cómo evitar deficiencias nutricionales</li>
            </ul>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;
